// Key of the custom "Vérifié" field, configured in Sharetribe Console with Metadata access
// level. Not present in local config since it's a hosted asset. Only the operator can set this
// value (not the provider). It exists both as a user field and, historically, as a listing
// field — see getVerifiedBadgeProps for which one wins.
export const VERIFIED_FIELD_KEY = 'verifie';
const VERIFIED_VALUE = 'oui';

// Key of the public listing field "Association professionnelle", configured in Sharetribe
// Console. Not present in local config since it's a hosted asset. Historically the only source
// of the badge's association value — see getVerifiedBadgeProps for the current fallback role.
export const ASSOCIATION_FIELD_KEY = 'association_professionnelle';

// Key of the custom USER field "association", public profile scope, configured in Sharetribe
// Console (Build > Content > User fields). Saved once per account as a multi-select, so a
// provider belonging to more than one association (e.g. ["rmpq", "fqm"]) lists all of them; the
// badge shows the first one.
export const AUTHOR_ASSOCIATION_FIELD_KEY = 'association';

/**
 * Resolves the "Vérifié [Association]" badge props for a listing.
 *
 * The badge is only shown when both conditions are true:
 * - the provider is verified, i.e. the `verifie` field is `oui`
 * - the provider has an association on file
 *
 * Both verification and association are granted/entered once per account by the operator or
 * provider, so the author's live profile is the source of truth for both: every listing of a
 * verified, associated provider gets the badge, including ones created before that profile data
 * was filled in. Listings that were set up individually before these fields moved to the account
 * level keep their own metadata/publicData as a fallback.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @param {Object} metadata listing.attributes.metadata
 * @param {Object} author the listing's author (user entity, e.g. from ensureUser)
 * @param {Object} config marketplace configuration (from useConfiguration())
 * @param {Object} intl react-intl object, used to translate the "Verified" word
 * @returns {Object|null} { label } or null if the badge shouldn't be shown
 */
export const getVerifiedBadgeProps = (publicData, metadata, author, config, intl) => {
  const authorPublicData = author?.attributes?.profile?.publicData;
  const authorMetadata = author?.attributes?.profile?.metadata;
  const isVerified =
    authorMetadata?.[VERIFIED_FIELD_KEY] === VERIFIED_VALUE ||
    metadata?.[VERIFIED_FIELD_KEY] === VERIFIED_VALUE;

  const authorAssociations = authorPublicData?.[AUTHOR_ASSOCIATION_FIELD_KEY];
  const associationValue =
    (Array.isArray(authorAssociations) && authorAssociations[0]) ||
    publicData?.[ASSOCIATION_FIELD_KEY];

  if (!isVerified || !associationValue) {
    return null;
  }

  const fieldConfig = config?.listing?.listingFields?.find(
    field => field.key === ASSOCIATION_FIELD_KEY
  );
  const optionConfig = fieldConfig?.enumOptions?.find(
    option => `${option.option}` === `${associationValue}`
  );
  // Note: this label comes from Sharetribe Console and only has one language version there.
  const associationLabel = optionConfig?.label || associationValue;

  const label = intl.formatMessage(
    { id: 'VerifiedBadge.label' },
    { association: associationLabel }
  );

  return { label };
};
